import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Send, Users, Briefcase, Globe, ArrowLeft, Bell,
  CheckCircle, AlertCircle, Trash2, Mail, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const AdminAnnouncements = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target: 'all',
    priority: 'normal',
    specific_email: ''
  });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(response.data.announcements || []);
    } catch (error) {
      console.error('Error fetching announcement history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) {
      setErrorMessage('Please fill in Title and Message');
      return;
    }
    if (formData.target === 'specific_email' && !formData.specific_email.trim()) {
      setErrorMessage('Please enter a specific email address');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('helply_token');
      const payload = {
        title: formData.title,
        message: formData.message,
        target: formData.target,
        priority: formData.priority,
        ...(formData.target === 'specific_email' && { specific_email: formData.specific_email.trim() })
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements/broadcast`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const inappCount = response.data.inapp_sent_count ?? 0;
      const emailCount = response.data.email_sent_count ?? 0;
      const totalUsers = response.data.total_users ?? 0;
      const warning = response.data.warning;

      let msg = `Sent to ${inappCount}/${totalUsers} users (in-app)`;
      if (emailCount > 0) msg += ` + ${emailCount} email(s)`;
      if (warning) msg += `. Note: Resend daily limit (100/day free tier) reached — emails will resume after midnight. Upgrade at resend.com/pricing for higher limits.`;
      setSuccessMessage(msg);

      setFormData({ title: '', message: '', target: 'all', priority: 'normal', specific_email: '' });
      fetchHistory();
      setTimeout(() => setSuccessMessage(''), 8000);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send announcement';
      setErrorMessage(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('Delete this announcement from history?')) return;
    setDeletingId(announcementId);
    try {
      const token = localStorage.getItem('helply_token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements/${announcementId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(prev => prev.filter(h => h.id !== announcementId));
      toast.success('Announcement deleted');
    } catch (error) {
      toast.error('Failed to delete announcement');
    } finally {
      setDeletingId(null);
    }
  };

  const getTargetIcon = (target) => {
    switch (target) {
      case 'clients': return <Users className="w-3 h-3" />;
      case 'professionals': return <Briefcase className="w-3 h-3" />;
      case 'specific_email': return <Mail className="w-3 h-3" />;
      default: return <Globe className="w-3 h-3" />;
    }
  };

  const getTargetColor = (target) => {
    switch (target) {
      case 'clients': return 'bg-blue-100 text-blue-800';
      case 'professionals': return 'bg-green-100 text-green-800';
      case 'specific_email': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-purple-100 text-purple-800';
    }
  };

  const getTargetLabel = (item) => {
    if (item.target === 'specific_email' && item.specific_email) return item.specific_email;
    return item.target;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold">Admin Announcements</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* ── Send Form ── */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center space-x-2 mb-6">
              <Bell className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold">Send New Announcement</h2>
            </div>

            {successMessage && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2" data-testid="success-msg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{successMessage}</p>
              </div>
            )}
            {errorMessage && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="error-msg">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. New Feature Available"
                  data-testid="announcement-title"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your announcement message..."
                  data-testid="announcement-message"
                  required
                />
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value, specific_email: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="announcement-target"
                >
                  <option value="all">All Users</option>
                  <option value="clients">Clients Only</option>
                  <option value="professionals">Professionals Only</option>
                  <option value="specific_email">Specific Email Address</option>
                </select>
              </div>

              {/* Specific Email — shown only when target is specific_email */}
              {formData.target === 'specific_email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.specific_email}
                    onChange={(e) => setFormData({ ...formData, specific_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="user@example.com"
                    data-testid="announcement-specific-email"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">The user must have a registered account with this email.</p>
                </div>
              )}

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="announcement-priority"
                >
                  <option value="normal">Normal</option>
                  <option value="high">High Priority</option>
                </select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="send-announcement-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send Announcement</>
                )}
              </Button>
            </form>

            {/* Info box */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-xs text-blue-700">
                <Bell className="w-3 h-3 inline mr-1" />
                Announcements are delivered as <strong>in-app notifications</strong> instantly. Email delivery uses Resend — free tier allows <strong>100 emails/day</strong>. Upgrade at <a href="https://resend.com/pricing" target="_blank" rel="noopener noreferrer" className="underline font-semibold">resend.com/pricing</a> for higher limits.
              </p>
            </div>
          </div>

          {/* ── History ── */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Recent Announcements</h2>

            {loadingHistory ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                <p className="text-gray-500 mt-2">Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No announcements sent yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[620px] overflow-y-auto pr-1">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition group"
                    data-testid={`announcement-${item.id}`}
                  >
                    {/* Row 1: Title + Target + Delete */}
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{item.title}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getTargetColor(item.target)}`}>
                          {getTargetIcon(item.target)}
                          {getTargetLabel(item)}
                        </span>
                        {item.priority === 'high' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex-shrink-0">High</span>
                        )}
                      </div>
                      {/* Delete button */}
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={deletingId === item.id}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0 p-1 rounded transition-colors"
                        title="Delete announcement"
                        data-testid={`delete-announcement-${item.id}`}
                      >
                        {deletingId === item.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Trash2 className="w-4 h-4" />
                        }
                      </button>
                    </div>

                    {/* Row 2: Message preview */}
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{item.message}</p>

                    {/* Row 3: Delivery stats + date */}
                    <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-gray-500">
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                        {(item.inapp_sent_count ?? 0) > 0 ? (
                          <span className="text-blue-600 font-medium flex items-center gap-0.5">
                            <Bell className="w-3 h-3" />{item.inapp_sent_count} in-app
                          </span>
                        ) : (
                          <span className="text-gray-400">0 in-app</span>
                        )}
                        {(item.email_sent_count ?? 0) > 0 && (
                          <span className="text-green-600 font-medium">✉ {item.email_sent_count} email</span>
                        )}
                        {(item.push_sent_count ?? 0) > 0 && (
                          <span className="text-purple-600 font-medium">📱 {item.push_sent_count} push</span>
                        )}
                        {item.quota_exceeded && (
                          <span className="text-orange-500 font-medium">Resend daily limit reached</span>
                        )}
                        {/* Old format fallback */}
                        {(item.inapp_sent_count == null) && (item.email_sent_count ?? 0) === 0 && (
                          <span className="text-gray-400 italic text-xs">Sent before in-app tracking</span>
                        )}
                      </div>
                      <span>{new Date(item.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncements;
